import { Link, Outlet } from '@remix-run/react'
import { Menu } from 'lucide-react'

import { Button } from '#app/components/ui/button.tsx'
import { ThemeSwitch } from '#app/routes/resources+/theme-switch.tsx'
import { useRequestInfo } from '#app/utils/request-info.ts'
import {
	Drawer,
	DrawerContent,
	DrawerHeader,
	DrawerTrigger,
} from '#app/components/ui/drawer.tsx'
import { cn } from '#app/utils/misc.tsx'

export default function Layout() {
	return (
		<div className="flex h-screen flex-col justify-between">
			<Navbar />
			<Outlet />
		</div>
	)
}

function Navbar() {
	return (
		<div className="flex h-screen flex-col justify-between">
			<div className="fixed inset-x-0 top-0 z-50 w-full border-b border-neutral-200 bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-800">
				<NavbarDesktop />
				<NavbarMobile />
			</div>
		</div>
	)
}

function NavbarDesktop() {
	const data = useRequestInfo()

	return (
		<div className="relative z-[60] mx-auto hidden w-full max-w-7xl flex-row items-center justify-between self-start rounded-full bg-transparent px-4 py-4 dark:bg-transparent sm:flex">
			<div className="flex items-center gap-8 text-sm">
				<Logo className="mr-2" />
				<Link to="#features" className="text-neutral-500 dark:text-neutral-400">
					Features
				</Link>
				<Link to="#pricing" className="text-neutral-500 dark:text-neutral-400">
					Pricing
				</Link>
			</div>
			<div className="relative flex items-center gap-4">
				<Link to="/login">
					<Button size="sm" variant="ghost">
						Login
					</Button>
				</Link>
				<Link to="https://cal.com/stanliu/subdark-demo" target="_blank">
					<Button size="sm">Book a demo</Button>
				</Link>
				<ThemeSwitch userPreference={data.userPrefs.theme} />
			</div>
		</div>
	)
}

function NavbarMobile() {
	return (
		<div className="relative z-50 mx-auto flex w-full max-w-[calc(100vw-2rem)] flex-col items-center justify-between bg-transparent px-0 py-4 sm:hidden">
			<div className="flex w-full flex-row items-center justify-between">
				<Logo />
				<Drawer>
					<DrawerTrigger asChild>
						<Menu className="cursor-pointer" />
					</DrawerTrigger>
					<DrawerContent className="bg-neutral-100 text-left dark:bg-neutral-900">
						<DrawerHeader>
							<NavItems className="my-4" />
						</DrawerHeader>
					</DrawerContent>
				</Drawer>
			</div>
		</div>
	)
}

function Logo({ className }: { className?: string }) {
	return (
		<Link
			to="/"
			className={cn(
				'relative z-10 flex cursor-pointer items-center gap-2 font-medium',
				className,
			)}
		>
			<img src="/favicons/favicon.svg" className="h-7 w-7" />
			<span className="hidden sm:block">Subdark</span>
		</Link>
	)
}

function NavItems({ className }: { className?: string }) {
	return (
		<div className={cn('flex h-full flex-col gap-6', className)}>
			<div className="grid gap-1 text-left">
				<Tab to="#features">Features</Tab>
				<Tab to="#pricing">Pricing</Tab>
				<Link to="/login">
					<Button className="w-full" variant="ghost">
						Login
					</Button>
				</Link>
				<Link to="https://cal.com/stanliu/subdark-demo" target="_blank">
					<Button className="w-full">Book a demo</Button>
				</Link>
			</div>
		</div>
	)
}

interface ITitle {
	children: React.ReactNode
}

function Title({ children }: ITitle) {
	return (
		<div className="min-h-[1.8rem] px-2 pb-3 text-left text-[0.7rem] font-bold uppercase text-neutral-500">
			{children}
		</div>
	)
}

interface ITab extends ITitle {
	to: string
}

function Tab({ children, to }: ITab) {
	const selected = false
	return (
		<Link
			to={to}
			className={cn(
				'hover:text-800/75 dark:hover:text-300/75 mb-1 flex min-h-[2.5rem] w-full cursor-pointer items-center gap-2 rounded-md bg-neutral-100 p-2 py-1.5 text-base text-neutral-900 hover:bg-neutral-200/75 dark:bg-neutral-900 dark:text-neutral-400 dark:hover:bg-neutral-800/75',
				selected &&
					'bg-neutral-700 text-neutral-200 hover:bg-neutral-700 hover:text-neutral-200',
				selected &&
					'dark:bg-neutral-300 dark:text-neutral-800 dark:hover:bg-neutral-300 dark:hover:text-neutral-800',
			)}
			target={to.startsWith('http') ? '_blank' : undefined}
		>
			{children}
		</Link>
	)
}
